import { useState, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { getBrandName } from "../../utility/branding";

const QRCodeDisplay = ({
  url,
  businessName = getBrandName(),
  size = 128,
  className = ""
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const qrContainerRef = useRef(null);

  const getSvgElement = () =>
    qrContainerRef.current?.querySelector('.qr-code-svg');

  const downloadQRCode = async () => {
    if (!url) return;

    setIsDownloading(true);

    try {
      const svgElement = getSvgElement();
      if (!svgElement) {
        setIsDownloading(false);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const qrSize = 512;

      canvas.width = qrSize;
      canvas.height = qrSize;

      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, qrSize, qrSize);
        ctx.drawImage(img, 0, 0, qrSize, qrSize);

        canvas.toBlob((blob) => {
          const downloadUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${businessName.toLowerCase().replace(/\s+/g, '-')}-qr-code.png`;
          link.href = downloadUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(downloadUrl);
          URL.revokeObjectURL(svgUrl);
          setIsDownloading(false);
        }, 'image/png');
      };

      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        setIsDownloading(false);
      };

      img.src = svgUrl;
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setIsDownloading(false);
    }
  };

  const printQRCode = () => {
    if (!url) return;

    const svgElement = getSvgElement();
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const printWindow = window.open('', '_blank', 'width=400,height=500');
    if (!printWindow) return;

    // Build DOM safely — no string interpolation of user-controlled values
    const doc = printWindow.document;
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            }
            .qr-container {
              text-align: center;
              padding: 2rem;
            }
            .qr-container svg {
              width: 256px;
              height: 256px;
            }
            .business-name {
              margin-top: 1rem;
              font-size: 1.25rem;
              font-weight: 600;
              color: #111;
            }
            .url {
              margin-top: 0.5rem;
              font-size: 0.875rem;
              color: #666;
              word-break: break-all;
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            ${svgData}
            <div class="business-name" id="qr-business-name"></div>
            <div class="url" id="qr-url"></div>
          </div>
        </body>
      </html>
    `);
    doc.close();

    // Set user-controlled text via textContent to prevent XSS
    doc.getElementById('qr-business-name').textContent = businessName;
    doc.getElementById('qr-url').textContent = url;

    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    };
  };

  if (!url) {
    return null;
  }

  return (
    <div ref={qrContainerRef} className={`flex flex-col items-center space-y-2 ${className}`}>
      {/* QR Code Label */}
      <span className="text-xs text-ink-500 font-medium">QR Code</span>

      {/* QR Code Image */}
      <div className="p-3 bg-white border border-ink-100 rounded-lg shadow-sm">
        <QRCodeSVG
          value={url}
          size={size}
          level="M"
          includeMargin={false}
          className="qr-code-svg"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={downloadQRCode}
          disabled={isDownloading}
          className="inline-flex items-center px-2 py-1.5 text-xs font-medium text-accent-600 bg-accent-100 border border-accent-200 rounded hover:bg-accent-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 min-h-[36px]"
          title="Download QR Code"
        >
          {isDownloading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b border-accent-600 mr-1"></div>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download
            </>
          )}
        </button>
        <button
          type="button"
          onClick={printQRCode}
          className="inline-flex items-center px-2 py-1.5 text-xs font-medium text-ink-600 bg-cream-100 border border-ink-100 rounded hover:bg-cream-200 transition-colors duration-200 min-h-[36px]"
          title="Print QR Code"
        >
          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print
        </button>
      </div>
    </div>
  );
};

export default QRCodeDisplay;
